import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd.length < 4) {
      setErr("סיסמה חייבת להיות בת 4 תווים לפחות");
      return;
    }
    if (pwd !== confirmPwd) {
      setErr("הסיסמאות לא זהות");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail) || "איפוס הסיסמה נכשל");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "linear-gradient(135deg, #F0FDF4 0%, #FEFCE8 100%)" }}>
        <div className="w-full max-w-md">
          <div className="rounded-3xl bg-white shadow-xl border p-8 text-center" style={{ borderColor: "#D1D5DB" }} data-testid="reset-no-token">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "#FEE2E2", color: "#991B1B" }}>
              <AlertCircle size={30} />
            </div>
            <h1 className="font-heebo font-black text-2xl text-slate-900 mb-2">קישור לא תקין</h1>
            <p className="text-sm text-slate-600 font-heebo mb-6">הקישור לאיפוס סיסמה שגוי או חסר. בקש קישור חדש.</p>
            <Link to="/forgot-password" className="btn-primary w-full justify-center inline-flex">
              בקש קישור חדש
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "linear-gradient(135deg, #F0FDF4 0%, #FEFCE8 100%)" }}>
      <div className="w-full max-w-md" data-testid="reset-page">
        <div className="rounded-3xl bg-white shadow-xl border p-8" style={{ borderColor: "#D1D5DB" }}>
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "#DCFCE7", color: "#166534" }}>
                <CheckCircle2 size={30} />
              </div>
              <h1 className="font-heebo font-black text-2xl text-slate-900 mb-2">הסיסמה הוחלפה!</h1>
              <p className="text-sm text-slate-600 font-heebo mb-6">מעבירים אותך למסך ההתחברות…</p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="mb-6">
                <h1 className="font-heebo font-black text-2xl text-slate-900 mb-1">איפוס סיסמה</h1>
                <p className="text-sm text-slate-500 font-heebo">הגדר סיסמה חדשה לחשבון שלך</p>
              </div>

              <label className="block mb-3">
                <div className="text-xs text-slate-500 mb-1 font-heebo">סיסמה חדשה</div>
                <div className="relative">
                  <input
                    type="text"
                    className={`input-field pr-12 ${show ? "" : "pw-masked"}`}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                    minLength={4}
                    data-testid="reset-password-input"
                    dir="ltr"
                    autoComplete="new-password"
                    style={{ paddingRight: "3rem" }}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-bwignore="true"
                    name="pw-reset"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    className="absolute inset-y-0 right-2 flex items-center px-2 text-slate-500 hover:text-slate-800 transition"
                    aria-label={show ? "הסתר" : "הצג"}
                    tabIndex={-1}
                  >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </label>

              <label className="block mb-4">
                <div className="text-xs text-slate-500 mb-1 font-heebo">אישור סיסמה</div>
                <input
                  type="text"
                  className={`input-field ${show ? "" : "pw-masked"}`}
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  required
                  minLength={4}
                  data-testid="reset-password-confirm"
                  dir="ltr"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  name="pw-reset-confirm"
                />
              </label>

              {err && (
                <div className="mb-4 chip chip-danger" data-testid="reset-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={busy || !pwd || !confirmPwd}
                className="btn-primary w-full justify-center"
                data-testid="reset-submit-btn"
              >
                {busy ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> מעדכן…
                  </span>
                ) : (
                  "עדכן סיסמה"
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
